import { useMemo } from 'react';

import { ColumnDef, SortingState } from '@tanstack/react-table';

import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';
import { useBusinessDevelopmentActions, useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import { TABLE_PIVOT, VIEW_BY_OPTIONS } from '../constants';

import { buildDischargesColumns, buildDischargesRows, DISCHARGES_DEFAULT_SORT } from './dischargesColumns';
import { buildPatientVolumeColumns, buildPatientVolumeRows, PATIENT_VOLUME_DEFAULT_SORT } from './patientVolumeColumns';
import { buildReferralsColumns, buildReferralsRows, REFERRALS_DEFAULT_SORT } from './referralsColumns';

type TableConfig = {
  columns: ColumnDef<any, any>[];
  data: Record<string, any>[];
  defaultSort: SortingState;
};

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function BusinessDevelopmentTable({ loading, rollups }: Props) {
  const viewBy = useBusinessDevelopmentStore((state) => state.viewBy);
  const tablePivot = useBusinessDevelopmentStore((state) => state.tablePivot);
  const groupTypeName = useBusinessDevelopmentStore((state) => state.selectedGroupType!.displayName);
  const { openSlideout } = useBusinessDevelopmentActions();

  const entityHeader = VIEW_BY_OPTIONS.find((o) => o.value === viewBy)!.label;
  const tableConfig = useMemo<TableConfig>(() => {
    switch (tablePivot) {
      case TABLE_PIVOT.DISCHARGES:
        return {
          columns: buildDischargesColumns(entityHeader, openSlideout),
          data: buildDischargesRows(rollups, viewBy),
          defaultSort: [DISCHARGES_DEFAULT_SORT],
        };
      case TABLE_PIVOT.REFERRALS:
        return {
          columns: buildReferralsColumns(entityHeader, openSlideout),
          data: buildReferralsRows(rollups, viewBy),
          defaultSort: [REFERRALS_DEFAULT_SORT],
        };
      default:
        return {
          columns: buildPatientVolumeColumns(entityHeader, openSlideout, groupTypeName),
          data: buildPatientVolumeRows(rollups, viewBy),
          defaultSort: [PATIENT_VOLUME_DEFAULT_SORT],
        };
    }
  }, [tablePivot, entityHeader, openSlideout, groupTypeName, viewBy, rollups]);

  return (
    <DataTable
      key={tablePivot}
      columns={tableConfig.columns}
      data={tableConfig.data}
      loading={loading}
      freezeFirstColumn
      hasFooter={false}
      defaultSortBy={tableConfig.defaultSort}
    />
  );
}
